import { queryStringFormat } from '@gbs-monorepo-packages/common';

import { type IApiThrowsError, type IResponseData, api } from './api';
import { type ICoursePageDTO, type ICoursePageListDTO } from './coursePages';
import { type IFontDTO, type IGlobalStyleDTO } from './courses';
import { type IClientFileDTO, type IDocumentDTO } from './documents';

export interface IViewCourseDataDTO {
  id: number;
  clientId: number;
  headerImage: IDocumentDTO | null;
  passcode?: string;
  passcodeProtected: boolean;
  title: string;
  viewURL: string | null;
  fonts: IFontDTO[];
  globalStyle: IGlobalStyleDTO | never[] | null;
  courseFile: IClientFileDTO | null;
}
interface IGetCourseProps {
  viewURL: string;
}

interface IPostCourseProps {
  viewURL: string;
  passcode: string;
}

interface IGetCoursePagesProps {
  viewURL: string;
  pageIds: string;
  language?: string;
  signal?: AbortSignal;
}

interface IPostCoursePagesProps {
  viewURL: string;
  pageIds: string;
  passcode: string;
  language?: string;
  signal?: AbortSignal;
}

export const getViewCourseData = async ({
  viewURL,
}: IGetCourseProps): Promise<IViewCourseDataDTO> => {
  const result = await api
    .get<IResponseData<IViewCourseDataDTO>>(
      `view-course/${viewURL}/course-data`
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};

export const postViewCourseData = async ({
  viewURL,
  passcode,
}: IPostCourseProps): Promise<IViewCourseDataDTO> => {
  const result = await api
    .post<IResponseData<IViewCourseDataDTO>>(
      `view-course/${viewURL}/course-data`,
      {
        passcode,
      }
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};

export const getViewCoursePagesList = async ({
  viewURL,
}: IGetCourseProps): Promise<ICoursePageListDTO[]> => {
  const result = await api
    .get<IResponseData<ICoursePageListDTO[]>>(
      `view-course/${viewURL}/course-pages-list`
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};

export const postViewCoursePagesList = async ({
  viewURL,
  passcode,
}: IPostCourseProps): Promise<ICoursePageListDTO[]> => {
  const result = await api
    .post<IResponseData<ICoursePageListDTO[]>>(
      `view-course/${viewURL}/course-pages-list`,
      {
        passcode,
      }
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};

export const getViewCoursePagesData = async ({
  viewURL,
  pageIds,
  language,
  signal,
}: IGetCoursePagesProps): Promise<ICoursePageDTO[]> => {
  const queryParams = queryStringFormat({
    pageIds,
    language: language ?? null,
  });
  const result = await api
    .get<IResponseData<ICoursePageDTO[]>>(
      `view-course/${viewURL}/course-pages-data?${queryParams}`,
      { signal }
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};

export const postViewCoursePagesData = async ({
  viewURL,
  pageIds,
  passcode,
  language,
  signal,
}: IPostCoursePagesProps): Promise<ICoursePageDTO[]> => {
  const queryParams = queryStringFormat({
    pageIds,
  });
  const result = await api
    .post<IResponseData<ICoursePageDTO[]>>(
      `view-course/${viewURL}/course-pages-data?${queryParams}`,
      {
        passcode,
        language,
      },
      { signal }
    )
    .then((resp) => resp.data.data)
    .catch((error: IApiThrowsError) => {
      throw error;
    });

  return result;
};
